import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search } from 'lucide-react';
import { listHubspotDeals } from '../services/integration-service';
import type { HubspotDeal } from '../types/integration.types';

interface Props {
  integrationId: string;
}

function formatAmount(v: string | null): string {
  if (!v) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'USD' }).format(n);
}

export function HubspotDealsTable({ integrationId }: Props) {
  const [items, setItems] = useState<HubspotDeal[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listHubspotDeals(integrationId, { search: search || undefined, page, pageSize })
      .then((r) => {
        if (cancelled) return;
        setItems(r.data);
        setTotal(r.pagination.total);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [integrationId, search, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar negocios..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          className="max-w-sm"
        />
        <span className="text-sm text-muted-foreground">{total} resultados</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Monto</TableHead>
            <TableHead>Etapa</TableHead>
            <TableHead>Pipeline</TableHead>
            <TableHead>Cierre estimado</TableHead>
            <TableHead>Empresa</TableHead>
            <TableHead>Última sync</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                Cargando...
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                Sin datos. Ejecutá una sincronización para ver registros aquí.
              </TableCell>
            </TableRow>
          ) : (
            items.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell>{formatAmount(d.amount)}</TableCell>
                <TableCell>{d.stage ?? '—'}</TableCell>
                <TableCell>{d.pipeline ?? '—'}</TableCell>
                <TableCell>
                  {d.closeDate ? new Date(d.closeDate).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>{d.hubspotCompany?.name ?? '—'}</TableCell>
                <TableCell>{new Date(d.lastSyncAt).toLocaleString()}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => p - 1)}
        >
          Anterior
        </Button>
        <span className="text-sm text-muted-foreground">
          Página {page} de {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((p) => p + 1)}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}
